import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import './Subscription.css';

export default function JoinWaitlist() {
  const location = useLocation();
  React.useEffect(() => {
    document.title = "Join Waitlist | LightTrade FOREX Academy";
  }, [location.pathname]);

  const { currentUser, subscriptionPlan } = useAuth();
  const navigate = useNavigate();
  const [loadingStates, setLoadingStates] = useState({
    intermediate: false,
    advanced: false
  });
  const [error, setError] = useState('');
  const [joined, setJoined] = useState(false);
  const [userPayments, setUserPayments] = useState(null);
  const [usdToGhs, setUsdToGhs] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);
  const [programPrices, setProgramPrices] = useState({
    intermediate: 50,
    advanced: 80
  });
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  // Redirect to login if not authenticated when trying to interact
  const requireAuth = (action) => {
    if (!currentUser) {
      // Store the current path to redirect back after login
      sessionStorage.setItem('redirectAfterLogin', '/join-waitlist');
      navigate('/login');
      return false;
    }
    return true;
  };

  // Handle payment with auth check and loading state
  const handlePaymentWithAuth = async (planId) => {
    if (!requireAuth('join waitlist')) return;
    
    // Set loading state for this specific plan
    setLoadingStates(prev => ({
      ...prev,
      [planId]: true
    }));
    
    try {
      await handlePayment(planId);
    } finally {
      // Reset loading state for this specific plan when done
      setLoadingStates(prev => ({
        ...prev,
        [planId]: false
      }));
    }
  };

  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

  // Fetch program prices from Firestore
  useEffect(() => {
    const fetchProgramPrices = async () => {
      try {
        // First try to get prices from Firestore
        try {
          const programsSnapshot = await getDocs(collection(db, 'programs'));
          const prices = {
            intermediate: 0,
            advanced: 0
          };
          
          // Get prices from Firestore
          programsSnapshot.forEach(doc => {
            const data = doc.data();
            if (doc.id === 'intermediate' && data.price) {
              prices.intermediate = data.price;
            } else if (doc.id === 'advanced' && data.price) {
              prices.advanced = data.price;
  
            }
          });
          
          setProgramPrices(prices);
        } catch (firebaseError) {
          console.warn("Couldn't fetch program prices from Firestore:", firebaseError);
          // Fallback to default prices if Firestore access fails
          setProgramPrices({
            intermediate: 50,
            advanced: 80
          });
        }
      } catch (error) {
        console.error("Unexpected error in fetchProgramPrices:", error);
      } finally {
        setPriceLoading(false);
      }
    };

    fetchProgramPrices();
  }, []);

  // Fetch exchange rate on component mount (no auth required)
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch exchange rate for everyone
        const exchangeRateResponse = await fetch('https://open.er-api.com/v6/latest/USD');
        const exchangeRateData = await exchangeRateResponse.json();
        setUsdToGhs(exchangeRateData.rates.GHS);

        // Only fetch user payments if user is logged in
        if (currentUser) {
          const paymentsDoc = await getDoc(doc(db, 'userPayments', currentUser.uid));
          if (paymentsDoc.exists()) {
            setUserPayments(paymentsDoc.data());
          }
        }
      } catch (error) {
        console.error("Exchange rate fetch error:", error);
      }
    };

    fetchData();
  }, [currentUser]);

  // Load Paystack script
  useEffect(() => {
    const loadPaystack = () => {
      return new Promise((resolve) => {
        if (window.PaystackPop) {
          setPaystackLoaded(true);
          return resolve(true);
        }

        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v1/inline.js';
        script.async = true;
        script.onload = () => {
          setPaystackLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          console.error('Failed to load Paystack script');
          toast.error('Failed to load payment service. Please refresh the page.');
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    loadPaystack();
  }, []);

  // Keep cohort label auto-generated by current date
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  let nextQuarterLabel;
  if (month < 3) nextQuarterLabel = `April ${year}`;
  else if (month < 6) nextQuarterLabel = `July ${year}`;
  else if (month < 9) nextQuarterLabel = `October ${year}`;
  else nextQuarterLabel = `January ${year + 1}`;

  // Handle payment (assumes user is already authenticated)
  const handlePayment = async (planId) => {
    // Don't proceed if already loading
    if (loadingStates[planId]) return;
    
    setError('');
    if (!requireAuth('join waitlist')) return;

    // Set loading state for this specific plan
    setLoadingStates(prev => ({
      ...prev,
      [planId]: true
    }));

    try {
      // Ensure Paystack is properly loaded
      if (!window.PaystackPop) {
        toast.error('Payment service is not available. Please refresh the page.', { autoClose: 5000 });
        return;
      }

      // Use fallback prices if Firestore fetch failed
      const priceInUSD = programPrices[planId] || (planId === 'intermediate' ? 50 : 80);
      const planPrice = usdToGhs ? Math.round(priceInUSD * usdToGhs) : priceInUSD * 12; // Fallback rate if exchange rate fetch fails
      
      if (!planPrice) {
        toast.error("Cannot process payment: price not available.", { autoClose: 5000 });
        return;
      }

      // Create a proper callback function reference
      const paymentCallback = function(response) {
        // Don't reset loading state here as we'll do it in the finally block
        toast.info('Payment completed! Verifying transaction...', { autoClose: 5000 });

        // Handle payment verification
        currentUser.getIdToken()
          .then(token => {
            return fetch('/api/verify-paystack', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                reference: response.reference,
                type: 'waitlist',
                plan: planId
              })
            });
          })
          .then(res => res.json())
          .then(data => {
            if (data.status === true || data.success) {
              // Update UI based on successful payment
              setJoined(true);
              toast.success('Payment verified successfully! You have been added to the waitlist.', { autoClose: 5000 });
            } else {
              toast.error(data.message || 'Payment verification failed. Please contact support.', { autoClose: 5000 });
            }
          })
          .catch(error => {
            console.error('Verification error:', error);
            toast.error('Error verifying payment. Please contact support.', { autoClose: 5000 });
          });
      };

      // Initialize Paystack payment
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: currentUser.email,
        amount: planPrice * 100, // Convert to kobo
        currency: 'GHS',
        ref: `LTF-WAITLIST-${planId.toUpperCase()}-${Date.now()}`,
        callback: paymentCallback,
        onClose: function() {
          // Don't show a message when the window is closed
          // This prevents the toast from showing when the payment is successful
        }
      });
      
      // Open the payment window
      handler.openIframe();

    } catch (error) {
      console.error('Payment initialization error:', error);
      setError('Failed to process payment. Please try again.');
      console.error('Payment error:', error);
      throw error; // Re-throw to be caught by handlePaymentWithAuth
    } finally {
      // Reset loading state for this specific plan when done
      setLoadingStates(prev => ({
        ...prev,
        [planId]: false
      }));
    }
  };

  const plans = [
    {
      id: "intermediate",
      name: "Intermediate",
      price: programPrices.intermediate,
      description: "Advanced technical analysis and risk management",
      features: [
        "Chart Patterns",
        "Risk Management Strategies",
        "Trading Psychology",
        "Market Structure Analysis"
      ]
    },
    {
      id: "advanced",
      name: "Advanced",
      price: programPrices.advanced,
      description: "Professional trading strategies and mentorship",
      features: [
        "Professional Trading Strategies",
        "Algorithmic Trading Basics",
        "Portfolio Management",
        "Weekly Live Trade Review Sessions"
      ]
    }
  ];

  return (
    <div className="subscription-page" style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative',
      zIndex: 1
    }}>
      <div className="subscription-header" style={{
        textAlign: 'center',
        marginBottom: '3rem',
        padding: '0 1rem'
      }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#2c5364',
          marginBottom: '0.5rem',
          lineHeight: '1.2'
        }}>Join the Waitlist</h1>
        <p style={{
          fontSize: '1.1rem',
          color: '#555',
          maxWidth: '800px',
          margin: '0 auto',
          lineHeight: '1.6'
        }}>Secure your spot for the <b>{nextQuarterLabel} Cohort</b> and get first access when enrollment opens.</p>
      </div>

      {currentUser && joined ? (
        <div className="success-message">
          <div className="success-icon">🎉</div>
          <h2>You're on the Waitlist!</h2>
          <p>Thank you for joining the waitlist for the <b>{nextQuarterLabel} Cohort</b>.</p>
          <p>We'll notify you as soon as your access is ready.</p>
          <button 
            className="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
        </div>
      ) : (
        <>
          {!currentUser && (
            <div className="auth-required-message" style={{
              textAlign: 'center',
              padding: '40px 20px',
              maxWidth: '760px',
              margin: '0 auto 2rem auto',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              <h2 style={{ color: '#2c3e50', marginBottom: '20px' }}>Create an Account or Sign In to Join</h2>
              <p style={{ fontSize: '16px', marginBottom: '30px', color: '#555' }}>
                Compare both tracks below. If you are new, sign up first. If you already have an account, sign in to continue your waitlist payment.
              </p>
              <div style={{
                display: 'flex',
                gap: '20px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <Link 
                  to="/register" 
                  style={{
                    padding: '12px 30px',
                    backgroundColor: '#0aa83f',
                    color: 'white',
                    textDecoration: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    border: '2px solid #0aa83f',
                    minWidth: '180px',
                    textAlign: 'center',
                    ':hover': {
                      backgroundColor: '#088a33',
                      borderColor: '#088a33'
                    }
                  }}
                  onClick={() => sessionStorage.setItem('redirectAfterRegister', '/join-waitlist')}
                >
                  Sign Up
                </Link>
                <Link 
                  to="/login" 
                  style={{
                    padding: '12px 30px',
                    backgroundColor: 'white',
                    color: '#0aa83f',
                    textDecoration: 'none',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: '2px solid #0aa83f',
                    transition: 'all 0.2s',
                    minWidth: '180px',
                    textAlign: 'center',
                    ':hover': {
                      backgroundColor: '#f0f0f0'
                    }
                  }}
                  onClick={() => sessionStorage.setItem('redirectAfterLogin', '/join-waitlist')}
                >
                  Login
                </Link>
              </div>
            </div>
          )}

          <div className="plans-container" style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '1.5rem',
          flexWrap: 'wrap',
          marginBottom: '3rem',
          padding: '0 1rem',
          boxSizing: 'border-box',
          width: '100%',
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto'
          }}>
          {plans.map((plan) => {
            // Skip rendering if user is already at or above this level
            // Skip rendering if user is already at or above this level
            if ((plan.id === 'intermediate' && (
                userPayments?.intermediatePaid ||
                userPayments?.advancedPaid ||
                subscriptionPlan === 'intermediate' ||
                subscriptionPlan === 'advanced')) ||
                (plan.id === 'advanced' && (
                  userPayments?.advancedPaid ||
                  subscriptionPlan === 'advanced'))) {
              return null;
            }
            
            const isCurrent = (plan.id === 'intermediate' && subscriptionPlan === 'intermediate') || 
                            (plan.id === 'advanced' && subscriptionPlan === 'advanced');

            const alreadyPaid = plan.id === 'intermediate' 
              ? userPayments?.intermediatePaid 
              : userPayments?.advancedPaid;

            return (
              <div 
                key={plan.id}
                className="plan-card"
                style={{
                  width: '100%',
                  maxWidth: '350px',
                  minHeight: '320px',
                  margin: '10px',
                  padding: '28px',
                  borderRadius: '18px',
                  background: 'linear-gradient(120deg, #f8fafc 80%, #e6f0fa 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  boxShadow: '0 8px 32px 0 rgba(44,83,100,0.11), 0 2px 8px 0 #00FFD022',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  zIndex: 1,
                  transform: 'translateZ(0)', // Force hardware acceleration
                  willChange: 'transform, box-shadow',
                  backfaceVisibility: 'hidden',
                  WebkitFontSmoothing: 'subpixel-antialiased'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.transform = 'translateY(-5px) scale(1.01)';
                  e.currentTarget.style.boxShadow = '0 12px 40px 0 rgba(44,83,100,0.15), 0 4px 12px 0 #00FFD033';
                  e.currentTarget.style.zIndex = '2';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(44,83,100,0.11), 0 2px 8px 0 #00FFD022';
                  e.currentTarget.style.zIndex = '1';
                }}
              >
                <h3 style={{ marginBottom: '8px', fontSize: '24px', fontWeight: 'bold' }}>
                  {plan.name} Track
                </h3>
                <p style={{ 
                  marginBottom: '12px', 
                  fontSize: '14px', 
                  color: '#666', 
                  textAlign: 'center', 
                  fontStyle: 'italic' 
                }}>
                  {plan.description}
                </p>
                <div style={{ margin: '18px 0', textAlign: 'center' }}>
                  {priceLoading ? (
                    <div>Loading price...</div>
                  ) : plan.price && usdToGhs ? (
                    <>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0aa83f' }}>
                        ${plan.price}/month
                      </div>
                    </>
                  ) : (
                    <div>Price unavailable</div>
                  )}
                </div>
                <div style={{ marginTop: 'auto', width: '100%' }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
                    {plan.features.map((feature, idx) => (
                      <li 
                        key={idx} 
                        style={{ 
                          fontSize: '14px', 
                          color: '#555', 
                          marginBottom: '8px',
                          paddingLeft: '20px',
                          position: 'relative'
                        }}
                      >
                        <span style={{ position: 'absolute', left: 0, color: '#0aa83f' }}>✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {currentUser ? (
                    <button
                      onClick={() => handlePaymentWithAuth(plan.id)}
                      disabled={loadingStates[plan.id] || !plan.price || alreadyPaid}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: alreadyPaid ? '#ccc' : '#0aa83f',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '16px',
                        cursor: alreadyPaid ? 'not-allowed' : 'pointer',
                        opacity: alreadyPaid ? 0.7 : 1,
                        marginTop: 'auto'
                      }}
                    >
                      {alreadyPaid 
                        ? 'Already Purchased' 
                        : loadingStates[plan.id]
                          ? 'Processing...' 
                          : 'Pay for 1 Month & Join Waitlist'}
                    </button>
                  ) : (
                    <>
                      <Link 
                        to="/register" 
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '12px',
                          backgroundColor: '#0aa83f',
                          color: '#fff',
                          textAlign: 'center',
                          textDecoration: 'none',
                          borderRadius: '6px',
                          fontWeight: 600,
                          fontSize: '16px',
                          cursor: 'pointer',
                          border: 'none',
                          marginTop: 'auto'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          sessionStorage.setItem('redirectAfterRegister', '/join-waitlist');
                          navigate('/register');
                        }}
                      >
                        Pay for 1 Month & Join Waitlist
                      </Link>
                      <Link
                        to="/login"
                        style={{
                          display: 'block',
                          marginTop: '10px',
                          textAlign: 'center',
                          color: '#0aa83f',
                          textDecoration: 'none',
                          fontSize: '14px',
                          fontWeight: 500
                        }}
                        onClick={() => sessionStorage.setItem('redirectAfterLogin', '/join-waitlist')}
                      >
                        Already registered? Login
                      </Link>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
      
      {error && (
        <div className="error-message" style={{
          color: '#dc3545',
          textAlign: 'center',
          margin: '20px 0',
          padding: '10px',
          backgroundColor: '#f8d7da',
          borderRadius: '4px',
          maxWidth: '800px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {error}
        </div>
      )}
      
      <div style={{ 
        margin: '32px auto', 
        fontSize: '14px', 
        color: '#888', 
        textAlign: 'center',
        padding: '0 20px',
        maxWidth: '800px',
        width: '100%',
        boxSizing: 'border-box' 
      }}>
        Already joined? We'll notify you when your cohort begins.
      </div>
    </div>
  );
}
